import { Suspense, lazy } from "react";
import FXBackdrop from "../components/FXBackdrop";
import PageTransition from "../components/PageTransition";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Calendar, FileText, Users, BarChart3, ShieldCheck } from "lucide-react";
import { NavLink } from "react-router-dom";
import SmartHireCTA from "../components/SmartHireCTA";

const Accent3D = lazy(() => import("../components/Accent3D"));

export default function Employers() {
  return (
    <PageTransition>
      <FXBackdrop>
        {/* HERO — light */}
        <section className="containerX pt-10 md:pt-16 pb-10 relative">
          <div className="float-shape bg-violet-400 h-14 w-14 top-4 right-16 hidden lg:block" />
          <div className="grid lg:grid-cols-2 gap-6 items-center">
            <div>
              <div className="chip">
                <span className="h-2 w-2 rounded-full" style={{ background: "rgb(139,92,246)" }} /> For Employers
              </div>
              <h1 className="h1 mt-5">Hire faster with <span className="text-black">higher signal.</span></h1>
              <p className="p mt-4 max-w-2xl">
                We run a transparent process: scorecards, vetting notes, and curated shortlists.
                Your team spends time interviewing, not filtering.
              </p>
              <div className="mt-7">
                <SmartHireCTA label="Start hiring" className="btn btnPrimary pulse-glow" />
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                <ShieldCheck size={15} className="text-violet-500 flex-shrink-0" />
                <span>90-day replacement guarantee on every placement</span>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="hidden lg:flex items-center justify-center"
            >
              <Suspense fallback={<div className="h-[280px]" />}>
                <Accent3D variant="employer" className="w-full h-[280px]" />
              </Suspense>
            </motion.div>
          </div>
        </section>

        {/* FEATURES — dark */}
        <section className="dark-section py-12 relative overflow-hidden">
          <div className="float-shape-slow bg-purple-500 h-24 w-24 -bottom-6 -right-6" style={{ animationDelay: "1s" }} />
          <div className="containerX">
            <div className="grid md:grid-cols-4 gap-4">
              {[
                [FileText, "Scorecards", "Consistent evaluation across candidates."],
                [Users, "Vetted Shortlist", "3 to 7 best matches, not 100 resumes."],
                [Calendar, "Fast Turnaround", "First matches in 72 hours."],
                [BarChart3, "Insights", "Market notes & hiring analytics."],
              ].map(([Icon, t, d], i) => (
                <motion.div
                  key={t} className="glass-dark p-6 group"
                  whileHover={{ y: -7, scale: 1.04, boxShadow: "0 20px 50px rgba(139,92,246,0.18)" }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <motion.div
                    className="inline-block"
                    whileHover={{ rotate: 12, scale: 1.15 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Icon size={18} className="text-violet-400" />
                  </motion.div>
                  <div className="font-semibold mt-3">{t}</div>
                  <div className="p mt-2 text-sm">{d}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ENGAGEMENT MODELS — dark */}
        <section className="dark-section py-12 md:py-16">
          <div className="containerX">
            <div className="chip">Engagement models</div>
            <h2 className="h2 mt-4">Choose the right hiring mode</h2>
            <p className="p mt-3 max-w-2xl">Flexible options depending on urgency and team size.</p>

            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {[
                ["Sprint", "For urgent roles", ["72h shortlist", "Fixed timeline", "Weekly updates"]],
                ["Dedicated", "For multiple hires", ["Full-cycle recruiting", "Interview loop mgmt", "Offer support"]],
                ["Pipeline", "For ongoing hiring", ["Monthly sourcing", "Bench building", "Market intel"]],
              ].map(([name, sub, bullets], i) => (
                <motion.div
                  key={name} className="glass-dark p-6"
                  whileHover={{ y: -8, scale: 1.03, boxShadow: "0 24px 60px rgba(139,92,246,0.18)" }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <div className="text-lg font-semibold">{name}</div>
                  <div className="text-sm text-white/70 mt-1">{sub}</div>
                  <div className="mt-4 grid gap-2">
                    {bullets.map((b) => (
                      <motion.div key={b} className="flex gap-2 items-start text-sm" whileHover={{ x: 4 }} transition={{ type: "spring", stiffness: 400 }}>
                        <CheckCircle2 size={16} className="mt-0.5 text-violet-400" />
                        <span className="text-white/80">{b}</span>
                      </motion.div>
                    ))}
                  </div>
                  <NavLink to="/contact" className="btn btnGhost mt-6 w-full">Get details</NavLink>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA — dark */}
        <section className="dark-section pb-16">
          <div className="containerX">
            <motion.div
              className="glass-dark p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
              whileHover={{ scale: 1.015, boxShadow: "0 28px 70px rgba(139,92,246,0.2)" }}
              transition={{ duration: 0.35 }}
            >
              <div>
                <div className="chip">Ready?</div>
                <div className="h2 mt-3">Send role requirements. Get candidates.</div>
                <p className="p mt-2 max-w-2xl">We'll reply with timeline + the sourcing strategy.</p>
              </div>
              <NavLink to="/contact" className="btn btnPrimary pulse-glow">Book a call <ArrowRight size={16} /></NavLink>
            </motion.div>
          </div>
        </section>
      </FXBackdrop>
    </PageTransition>
  );
}
